import React, { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import {
    Tabs, TabsList, TabsTrigger, TabsContent
} from "../components/ui/tabs";
import { Plus, Trash2, UserCog, History, Pencil, Eye, EyeOff, Send, Phone, Hash, ChevronDown, ChevronUp, Clock, Crown, Activity, AlertTriangle, MessageCircle, Zap, Check, X as XIcon, KeyRound, Copy, Mail } from "lucide-react";
import SharedMailboxesAdmin from "../components/SharedMailboxesAdmin";
import MicrosoftSetupChecker from "../components/MicrosoftSetupChecker";
import MailPoolAdmin from "../components/MailPoolAdmin";
import { toast } from "sonner";

// User is considered online if last_seen is within last 90 seconds
function isOnline(u) {
    if (!u?.last_seen) return false;
    const diff = (Date.now() - new Date(u.last_seen).getTime()) / 1000;
    return diff < 90;
}

function fmtLastSeen(iso, lang) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return lang === "de" ? "jetzt" : lang === "tr" ? "şimdi" : "now";
    if (diff < 3600) return `${Math.floor(diff / 60)} ${lang === "de" ? "Min." : "min"}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${lang === "de" ? "Std." : "h"}`;
    return d.toLocaleDateString(LOCALES[lang]);
}

export default function AdminPage() {
    const { user, lang } = useAuth();
    const [users, setUsers] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [deletionLog, setDeletionLog] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [auditFilter, setAuditFilter] = useState({ entity_type: "", action: "" });
    const [deletionRequests, setDeletionRequests] = useState([]);
    const [channelRules, setChannelRules] = useState([]);
    const [scChannels, setScChannels] = useState([]);
    const [pinResets, setPinResets] = useState([]);
    const [approvedTempPins, setApprovedTempPins] = useState({}); // requestId -> temp_pin (memory-only)
    const [ruleEditOpen, setRuleEditOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);  // null = new, otherwise rule object
    const [newOpen, setNewOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);

    const load = useCallback(async () => {
        const [u, sp, dl, dr, cr, ch, pr] = await Promise.all([
            api.get("/users"),
            api.get("/spaces"),
            api.get("/deletion-log").catch(() => ({ data: [] })),
            api.get("/admin/deletion-requests").catch(() => ({ data: { results: [] } })),
            api.get("/superchat/channel-rules").catch(() => ({ data: { results: [] } })),
            api.get("/superchat/channels").catch(() => ({ data: { data: { results: [] } } })),
            api.get("/pin-reset-requests").catch(() => ({ data: [] })),
        ]);
        setUsers(u.data);
        setSpaces(sp.data);
        setDeletionLog(dl.data);
        setDeletionRequests(dr.data?.results || []);
        setChannelRules(cr.data?.results || []);
        setScChannels(ch.data?.data?.results || []);
        setPinResets(pr.data || []);
    }, []);

    const loadAudit = useCallback(async () => {
        const q = new URLSearchParams();
        if (auditFilter.entity_type) q.set("entity_type", auditFilter.entity_type);
        if (auditFilter.action) q.set("action", auditFilter.action);
        q.set("limit", "300");
        const { data } = await api.get(`/audit-log?${q.toString()}`);
        setAuditLog(data);
    }, [auditFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadAudit(); }, [loadAudit]);

    const deleteUser = async (id) => {
        if (!window.confirm(lang === "tr" ? "Bu kullanıcıyı silmek istiyor musunuz?" : lang === "de" ? "Diesen Benutzer löschen?" : "Delete this user?")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success(t(lang, "delete"));
            load();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const approveDeletion = async (req) => {
        const confirmMsg = lang === "de"
            ? `Wirklich endgültig löschen?\n\n${req.entity_type === "superchat_conv" ? "Konversation: " : req.entity_type === "task" ? "Aufgabe: " : "Rechnung: "}${req.title}`
            : `Permanently delete?\n\n${req.title}`;
        if (!window.confirm(confirmMsg)) return;
        try {
            if (req.entity_type === "task") await api.delete(`/tasks/${req.entity_id}`);
            else if (req.entity_type === "invoice") await api.delete(`/invoices/${req.entity_id}`);
            else if (req.entity_type === "superchat_conv") await api.delete(`/superchat/conversations/${req.entity_id}`);
            toast.success(lang === "de" ? "Gelöscht" : "Deleted");
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const rejectDeletion = async (req) => {
        if (!window.confirm(lang === "de" ? "Löschantrag ablehnen?" : "Reject deletion request?")) return;
        try {
            if (req.entity_type === "task") await api.post(`/tasks/${req.entity_id}/cancel-deletion-request`);
            else if (req.entity_type === "invoice") await api.post(`/invoices/${req.entity_id}/cancel-deletion-request`);
            else if (req.entity_type === "superchat_conv") await api.post(`/superchat/conversations/${req.entity_id}/cancel-deletion-request`);
            toast.success(lang === "de" ? "Abgelehnt" : "Rejected");
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const saveRule = async (form) => {
        try {
            if (editingRule?.id) {
                await api.patch(`/superchat/channel-rules/${editingRule.id}`, {
                    space_ids: form.space_ids,
                    priority: form.priority,
                    enabled: form.enabled,
                });
            } else {
                await api.post(`/superchat/channel-rules`, form);
            }
            toast.success(lang === "de" ? "Regel gespeichert" : "Rule saved");
            setRuleEditOpen(false);
            setEditingRule(null);
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const deleteRule = async (id) => {
        if (!window.confirm(lang === "de" ? "Regel löschen?" : "Delete rule?")) return;
        try {
            await api.delete(`/superchat/channel-rules/${id}`);
            toast.success(lang === "de" ? "Gelöscht" : "Deleted");
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    if (user?.role !== "admin" && user?.role !== "master") {
        return <div className="p-8 text-neutral-500">No access</div>;
    }

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="admin-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-1">{t(lang, "admin")}</p>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
                        {lang === "tr" ? "Yönetim Paneli" : lang === "de" ? "Administration" : "Admin Panel"}
                    </h1>
                </div>
                <div className="flex gap-2">
                <Button onClick={() => setNewOpen(true)} className="bg-neutral-900 hover:bg-neutral-800" data-testid="add-user-btn">
                    <Plus className="w-4 h-4 mr-1" /> {t(lang, "addWorker")}
                </Button>
                <Button onClick={() => setInviteOpen(true)} variant="outline" data-testid="invite-user-btn">
                    <Send className="w-4 h-4 mr-1" />{t(lang, "inviteUser")}
                </Button>
                </div>
            </div>

            <Tabs defaultValue="users">
                <TabsList>
                    <TabsTrigger value="users" data-testid="tab-users"><UserCog className="w-3.5 h-3.5 mr-1.5" />{t(lang, "workers")}</TabsTrigger>
                    <TabsTrigger value="audit" data-testid="tab-audit"><Activity className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Aktivitätsverlauf" : "Audit Log"}</TabsTrigger>
                    <TabsTrigger value="deletion-requests" data-testid="tab-deletion-requests">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                        {lang === "de" ? "Löschanträge" : "Deletion Requests"}
                        {deletionRequests.length > 0 && (
                            <span className="ml-1.5 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{deletionRequests.length}</span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="sc-rules" data-testid="tab-sc-rules"><Zap className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Superchat-Regeln" : "Superchat Rules"}</TabsTrigger>
                    <TabsTrigger value="pin-resets" data-testid="tab-pin-resets">
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                        {lang === "de" ? "PIN-Anträge" : "PIN requests"}
                        {pinResets.filter((r) => r.status === "pending").length > 0 && (
                            <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pinResets.filter((r) => r.status === "pending").length}</span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="shared-mailboxes" data-testid="tab-shared-mailboxes">
                        <Mail className="w-3.5 h-3.5 mr-1.5" />
                        {lang === "de" ? "Postfächer" : "Mailboxes"}
                    </TabsTrigger>
                    <TabsTrigger value="log" data-testid="tab-log"><History className="w-3.5 h-3.5 mr-1.5" />{t(lang, "deletionLog")}</TabsTrigger>
                </TabsList>
                <TabsContent value="users">
                    <div className="rounded-lg border border-neutral-200 bg-white overflow-x-auto" data-testid="users-table">
                        <table className="w-full text-sm min-w-[760px]">
                            <thead className="bg-neutral-50 text-neutral-600 text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "name")}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "email")}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "role")}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "phone")}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "personnelNumber")}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold">{t(lang, "spaces")}</th>
                                    <th className="text-right px-4 py-2.5 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200">
                                {users.map((u) => {
                                    const online = isOnline(u);
                                    return (
                                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-neutral-50">
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <div className="relative">
                                                    <div className="w-7 h-7 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center">
                                                        {u.name?.slice(0, 2)?.toUpperCase()}
                                                    </div>
                                                    <span
                                                        data-testid={`user-online-indicator-${u.id}`}
                                                        title={online ? t(lang, "online") : `${t(lang, "lastSeen")}: ${fmtLastSeen(u.last_seen, lang)}`}
                                                        className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-neutral-300"}`}
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium leading-tight">{u.name}</p>
                                                    {u.job_title && <p className="text-[10px] text-neutral-500 leading-tight">{u.job_title}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-neutral-600">{u.email}</td>
                                        <td className="px-4 py-2.5">
                                            <Badge variant={u.role === "admin" ? "default" : "outline"} className="text-[10px] uppercase">{u.role}</Badge>
                                            {u.acting_until && (
                                                <Badge variant="outline" className="ml-1 text-[9px] bg-amber-50 border-amber-300 text-amber-800" title={u.acting_reason || ""}>
                                                    <Crown className="w-2.5 h-2.5 mr-0.5" /> {lang === "de" ? "bis" : "until"} {new Date(u.acting_until).toLocaleDateString(LOCALES[lang], { day: "2-digit", month: "2-digit" })}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-neutral-600 text-xs">{u.phone || "—"}</td>
                                        <td className="px-4 py-2.5 text-neutral-600 text-xs font-mono">{u.personnel_number || "—"}</td>
                                        <td className="px-4 py-2.5 text-xs text-neutral-600 max-w-[200px] truncate">
                                            {(u.space_ids || []).map((sid) => spaces.find((s) => s.id === sid)?.name).filter(Boolean).join(", ") || "—"}
                                        </td>
                                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => setEditUser(u)}
                                                data-testid={`edit-user-${u.id}`}
                                                className="text-neutral-600 hover:text-neutral-900 p-1 mr-1"
                                                title={t(lang, "edit")}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            {u.id !== user.id && (
                                                <button
                                                    onClick={() => deleteUser(u.id)}
                                                    data-testid={`delete-user-${u.id}`}
                                                    className="text-red-600 hover:text-red-800 p-1"
                                                    title={t(lang, "delete")}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
                <TabsContent value="audit">
                    <div className="rounded-lg border border-neutral-200 bg-white">
                        <div className="flex items-center gap-2 p-3 border-b border-neutral-200 flex-wrap">
                            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{lang === "de" ? "Filter" : "Filter"}:</span>
                            <Select value={auditFilter.entity_type || "all"} onValueChange={(v) => setAuditFilter((p) => ({ ...p, entity_type: v === "all" ? "" : v }))}>
                                <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="audit-filter-type"><SelectValue placeholder={lang === "de" ? "Typ" : "Type"} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{lang === "de" ? "Alle Typen" : "All types"}</SelectItem>
                                    <SelectItem value="task">{lang === "de" ? "Aufgaben" : "Tasks"}</SelectItem>
                                    <SelectItem value="invoice">Banking</SelectItem>
                                    <SelectItem value="space">{lang === "de" ? "Abteilungen" : "Departments"}</SelectItem>
                                    <SelectItem value="user">User</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={auditFilter.action || "all"} onValueChange={(v) => setAuditFilter((p) => ({ ...p, action: v === "all" ? "" : v }))}>
                                <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="audit-filter-action"><SelectValue placeholder={lang === "de" ? "Aktion" : "Action"} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{lang === "de" ? "Alle Aktionen" : "All actions"}</SelectItem>
                                    <SelectItem value="created">{lang === "de" ? "Erstellt" : "Created"}</SelectItem>
                                    <SelectItem value="updated">{lang === "de" ? "Bearbeitet" : "Updated"}</SelectItem>
                                    <SelectItem value="deleted">{lang === "de" ? "Gelöscht" : "Deleted"}</SelectItem>
                                    <SelectItem value="requested_deletion">{lang === "de" ? "Löschantrag" : "Deletion Request"}</SelectItem>
                                    <SelectItem value="role_changed">{lang === "de" ? "Rollenwechsel" : "Role Changed"}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button onClick={loadAudit} size="sm" variant="outline" className="h-8 ml-auto" data-testid="audit-refresh">{lang === "de" ? "Aktualisieren" : "Refresh"}</Button>
                        </div>
                        <div className="overflow-x-auto" data-testid="audit-log-list">
                            <table className="w-full text-xs min-w-[680px]">
                                <thead className="bg-neutral-50 text-neutral-600 text-[10px] uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Zeit" : "Time"}</th>
                                        <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Wer" : "Who"}</th>
                                        <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Aktion" : "Action"}</th>
                                        <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Objekt" : "Entity"}</th>
                                        <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Details" : "Details"}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100">
                                    {auditLog.length === 0 ? (
                                        <tr><td colSpan={5} className="px-4 py-6 text-center text-neutral-500">—</td></tr>
                                    ) : auditLog.map((l) => (
                                        <tr key={l.id} className="hover:bg-neutral-50" data-testid={`audit-row-${l.id}`}>
                                            <td className="px-3 py-2 whitespace-nowrap text-neutral-500">{new Date(l.timestamp).toLocaleString(LOCALES[lang])}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="font-medium">{l.actor_name}</span>
                                                <Badge variant="outline" className="ml-1.5 text-[9px] uppercase">{l.actor_role}</Badge>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <Badge className={`text-[10px] ${
                                                    l.action === "deleted" ? "bg-red-100 text-red-700 border-red-200" :
                                                    l.action === "created" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                                                    l.action === "requested_deletion" ? "bg-amber-100 text-amber-700 border-amber-200" :
                                                    l.action === "role_changed" ? "bg-purple-100 text-purple-700 border-purple-200" :
                                                    "bg-blue-100 text-blue-700 border-blue-200"
                                                }`} variant="outline">{l.action}</Badge>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="font-medium">{l.entity_title || "—"}</span>
                                                <span className="ml-1.5 text-[10px] text-neutral-400 uppercase">{l.entity_type}</span>
                                            </td>
                                            <td className="px-3 py-2 text-neutral-600">{l.details || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="deletion-requests">
                    <div className="rounded-lg border border-neutral-200 bg-white p-1" data-testid="deletion-requests-list">
                        {deletionRequests.length === 0 ? (
                            <p className="p-8 text-sm text-neutral-500 text-center">{lang === "de" ? "Keine offenen Löschanträge." : "No pending deletion requests."}</p>
                        ) : (
                            <ul className="divide-y divide-neutral-100">
                                {deletionRequests.map((r) => {
                                    const typeLabel = r.entity_type === "task" ? (lang === "de" ? "Aufgabe" : "Task") : r.entity_type === "invoice" ? (lang === "de" ? "Rechnung" : "Invoice") : (lang === "de" ? "Chat-Konversation" : "Conversation");
                                    const typeColor = r.entity_type === "task" ? "bg-blue-100 text-blue-700" : r.entity_type === "invoice" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
                                    return (
                                        <li key={`${r.entity_type}-${r.entity_id}`} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`del-req-${r.entity_id}`}>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${typeColor}`}>{typeLabel}</span>
                                                    <p className="font-semibold text-sm truncate">{r.title}</p>
                                                    {r.subtitle && <span className="text-[11px] text-neutral-500">· {r.subtitle}</span>}
                                                </div>
                                                <p className="text-[11px] text-neutral-600">
                                                    {lang === "de" ? "Beantragt von" : "Requested by"} <strong>{r.requested_by_name}</strong>
                                                    {" · "}{new Date(r.requested_at).toLocaleString(LOCALES[lang])}
                                                </p>
                                                {r.reason && <p className="text-[11px] text-neutral-700 mt-1 italic">&ldquo;{r.reason}&rdquo;</p>}
                                            </div>
                                            <div className="flex gap-1.5 shrink-0">
                                                <Button size="sm" variant="outline" onClick={() => rejectDeletion(r)} className="h-8 border-neutral-300" data-testid={`del-req-reject-${r.entity_id}`}>
                                                    <XIcon className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Ablehnen" : "Reject"}
                                                </Button>
                                                <Button size="sm" onClick={() => approveDeletion(r)} className="h-8 bg-red-600 hover:bg-red-700 text-white" data-testid={`del-req-approve-${r.entity_id}`}>
                                                    <Check className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Genehmigen" : "Approve"}
                                                </Button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </TabsContent>
                <TabsContent value="sc-rules">
                    <div className="space-y-3" data-testid="sc-rules-list">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-neutral-600">
                                {lang === "de"
                                    ? "Eingehende Nachrichten auf einem Channel werden automatisch als Aufgabe in den ausgewählten Abteilungen angelegt."
                                    : "Incoming messages on a channel are auto-created as a task in the selected departments."}
                            </p>
                            <Button onClick={() => { setEditingRule(null); setRuleEditOpen(true); }} size="sm" data-testid="sc-rule-add">
                                <Plus className="w-4 h-4 mr-1" />{lang === "de" ? "Neue Regel" : "New Rule"}
                            </Button>
                        </div>
                        <div className="rounded-lg border border-neutral-200 bg-white">
                            {channelRules.length === 0 ? (
                                <p className="p-8 text-sm text-neutral-500 text-center">{lang === "de" ? "Noch keine Regeln. Klicke auf Neue Regel." : "No rules yet."}</p>
                            ) : (
                                <ul className="divide-y divide-neutral-100">
                                    {channelRules.map((r) => (
                                        <li key={r.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`sc-rule-${r.id}`}>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <MessageCircle className="w-4 h-4 text-neutral-400 shrink-0" />
                                                    <p className="font-semibold text-sm truncate">{r.channel_name}</p>
                                                    <span className="text-[10px] text-neutral-500 uppercase">{r.channel_type}</span>
                                                    {!r.enabled && <span className="text-[10px] font-bold bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded">{lang === "de" ? "AUS" : "OFF"}</span>}
                                                </div>
                                                <p className="text-[11px] text-neutral-600">
                                                    {lang === "de" ? "Aufgaben in" : "Tasks in"}: <strong>{(r.space_ids || []).map(id => spaces.find(s => s.id === id)?.name || id).join(", ") || "—"}</strong>
                                                    {" · "}{lang === "de" ? "Priorität" : "Priority"}: <strong>{r.priority}</strong>
                                                </p>
                                            </div>
                                            <div className="flex gap-1.5">
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingRule(r); setRuleEditOpen(true); }} className="h-8" data-testid={`sc-rule-edit-${r.id}`}>
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => deleteRule(r.id)} className="h-8 text-red-600 hover:bg-red-50" data-testid={`sc-rule-del-${r.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="pin-resets">
                    <div className="rounded-lg border border-neutral-200 bg-white p-1" data-testid="pin-resets-list">
                        {pinResets.length === 0 ? (
                            <p className="p-6 text-sm text-neutral-500 text-center">{lang === "de" ? "Keine offenen PIN-Anträge" : "No pending PIN requests"}</p>
                        ) : (
                            <ul className="divide-y divide-neutral-100">
                                {pinResets.map((r) => (
                                    <li key={r.id} className="px-4 py-3" data-testid={`pin-reset-${r.id}`}>
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold">{r.note_title || (lang === "de" ? "Notiz" : "Note")} <span className="text-[10px] text-neutral-500 ml-1">{r.pin_mode === "individual" ? "Eigene PIN/User" : "Einheitliche PIN"}</span></p>
                                                <p className="text-xs text-neutral-600">{r.user_name} ({r.user_email})</p>
                                                {r.reason && <p className="text-xs text-neutral-500 italic mt-1">&bdquo;{r.reason}&ldquo;</p>}
                                                <p className="text-[10px] text-neutral-400 mt-1">{new Date(r.requested_at).toLocaleString(LOCALES[lang])}</p>
                                            </div>
                                            {r.status === "pending" ? (
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <Button size="sm" onClick={async () => {
                                                        try {
                                                            const { data } = await api.post(`/pin-reset-requests/${r.id}/approve`);
                                                            setApprovedTempPins((prev) => ({ ...prev, [r.id]: data.temp_pin }));
                                                            toast.success(lang === "de" ? "Temporäre PIN erstellt — bitte dem Nutzer übermitteln" : "Temp PIN generated — share with user");
                                                            await load();
                                                        } catch (e) { toast.error(errMsg(e)); }
                                                    }} className="bg-green-500 hover:bg-green-600 text-white h-7" data-testid={`pin-approve-${r.id}`}>
                                                        <Check className="w-3 h-3 mr-1" /> {lang === "de" ? "Freischalten" : "Approve"}
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={async () => {
                                                        try { await api.post(`/pin-reset-requests/${r.id}/reject`); await load(); }
                                                        catch (e) { toast.error(errMsg(e)); }
                                                    }} className="h-7" data-testid={`pin-reject-${r.id}`}>
                                                        <XIcon className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
                                            )}
                                        </div>
                                        {approvedTempPins[r.id] && (
                                            <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
                                                <KeyRound className="w-4 h-4 text-amber-600" />
                                                <div className="flex-1">
                                                    <p className="text-[11px] text-amber-700 font-semibold">{lang === "de" ? "Temporäre PIN — nur einmal sichtbar!" : "Temporary PIN — shown only once!"}</p>
                                                    <code className="text-base font-mono font-bold tracking-widest text-amber-900" data-testid={`temp-pin-${r.id}`}>{approvedTempPins[r.id]}</code>
                                                </div>
                                                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(approvedTempPins[r.id]); toast.success(lang === "de" ? "Kopiert" : "Copied"); }} className="h-6" data-testid={`copy-temp-pin-${r.id}`}>
                                                    <Copy className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>
                <TabsContent value="shared-mailboxes">
                    <div className="rounded-lg border border-neutral-200 bg-white p-3 mb-3" data-testid="ms-setup-section">
                        <MicrosoftSetupChecker lang={lang} />
                    </div>
                    <div className="rounded-lg border border-neutral-200 bg-white p-3 mb-3" data-testid="mail-pool-section">
                        <MailPoolAdmin lang={lang} />
                    </div>
                    <div className="rounded-lg border border-neutral-200 bg-white p-3" data-testid="shared-mailboxes-section">
                        <SharedMailboxesAdmin lang={lang} />
                    </div>
                </TabsContent>
                <TabsContent value="log">
                    <div className="rounded-lg border border-neutral-200 bg-white p-1" data-testid="deletion-log-list">
                        {deletionLog.length === 0 ? (
                            <p className="p-6 text-sm text-neutral-500 text-center">—</p>
                        ) : (
                            <ul className="divide-y divide-neutral-100">
                                {deletionLog.map((l) => (
                                    <li key={l.id} className="px-4 py-3 text-sm">
                                        <p>
                                            <span className="font-semibold">{l.deleted_by_name}</span>{" "}
                                            <span className="text-neutral-600">{lang === "tr" ? "sildi" : lang === "de" ? "löschte" : "deleted"}:</span>{" "}
                                            <span className="font-medium">{l.task_snapshot?.title}</span>
                                        </p>
                                        <p className="text-[11px] text-neutral-500 mt-0.5">
                                            {new Date(l.deleted_at).toLocaleString(LOCALES[lang])}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            <NewWorkerDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} spaces={spaces} lang={lang} />
            <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={load} spaces={spaces} lang={lang} />
            <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onUpdated={() => { load(); loadAudit(); }} spaces={spaces} lang={lang} actor={user} />
            <ChannelRuleDialog
                open={ruleEditOpen}
                onClose={() => { setRuleEditOpen(false); setEditingRule(null); }}
                onSave={saveRule}
                rule={editingRule}
                channels={scChannels}
                spaces={spaces}
                lang={lang}
            />
        </div>
    );
}

function ChannelRuleDialog({ open, onClose, onSave, rule, channels, spaces, lang }) {
    const [channelId, setChannelId] = useState("");
    const [spaceIds, setSpaceIds] = useState([]);
    const [priority, setPriority] = useState("medium");
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (open) {
            setChannelId(rule?.channel_id || (channels[0]?.id || ""));
            setSpaceIds(rule?.space_ids || []);
            setPriority(rule?.priority || "medium");
            setEnabled(rule?.enabled !== false);
        }
    }, [open, rule, channels]);

    const toggleSpace = (sid) => {
        setSpaceIds((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);
    };

    const submit = () => {
        if (!channelId) {
            toast.error(lang === "de" ? "Bitte Channel wählen" : "Pick a channel");
            return;
        }
        if (spaceIds.length === 0) {
            toast.error(lang === "de" ? "Bitte mindestens eine Abteilung wählen" : "Pick at least one department");
            return;
        }
        onSave({ channel_id: channelId, space_ids: spaceIds, priority, enabled });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="sc-rule-dialog">
                <DialogHeader>
                    <DialogTitle>{rule ? (lang === "de" ? "Regel bearbeiten" : "Edit rule") : (lang === "de" ? "Neue Auto-Task-Regel" : "New auto-task rule")}</DialogTitle>
                    <DialogDescription>{lang === "de" ? "Eingehende Nachrichten auf dem Channel werden in den Abteilungs-Kanban als Aufgabe angelegt." : "Incoming messages auto-create a task in the selected departments."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold">{lang === "de" ? "Superchat-Channel" : "Channel"}</Label>
                        <Select value={channelId} onValueChange={setChannelId} disabled={!!rule}>
                            <SelectTrigger data-testid="sc-rule-channel"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {channels.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.type})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">{lang === "de" ? "Abteilungen (mehrfach möglich)" : "Departments (multi)"}</Label>
                        <div className="grid grid-cols-2 gap-1.5 mt-1">
                            {spaces.map((s) => (
                                <label key={s.id} className={`flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer text-xs ${spaceIds.includes(s.id) ? "bg-blue-50 border-blue-300" : "bg-white border-neutral-200 hover:bg-neutral-50"}`}>
                                    <input
                                        type="checkbox"
                                        checked={spaceIds.includes(s.id)}
                                        onChange={() => toggleSpace(s.id)}
                                        data-testid={`sc-rule-space-${s.id}`}
                                    />
                                    <span className="truncate">{s.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">{lang === "de" ? "Priorität" : "Priority"}</Label>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger data-testid="sc-rule-priority"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">{lang === "de" ? "Niedrig" : "Low"}</SelectItem>
                                    <SelectItem value="medium">{lang === "de" ? "Mittel" : "Medium"}</SelectItem>
                                    <SelectItem value="high">{lang === "de" ? "Hoch" : "High"}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">{lang === "de" ? "Aktiviert" : "Enabled"}</Label>
                            <div className="mt-2"><label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="sc-rule-enabled" />
                                <span>{enabled ? (lang === "de" ? "Aktiv" : "Active") : (lang === "de" ? "Inaktiv" : "Inactive")}</span>
                            </label></div>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="sc-rule-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={submit} className="bg-neutral-900 hover:bg-neutral-800" data-testid="sc-rule-save">{lang === "de" ? "Speichern" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InviteDialog({ open, onClose, onCreated, spaces, lang }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("user");
    const [spaceIds, setSpaceIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const [link, setLink] = useState("");

    useEffect(() => {
        if (open) { setName(""); setEmail(""); setRole("user"); setSpaceIds([]); setLink(""); }
    }, [open]);

    const submit = async () => {
        if (!name.trim() || !email.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/users/invite", { name, email, role, space_ids: spaceIds });
            setLink(data.invite_link);
            toast.success(lang === "tr" ? "Davet gönderildi" : lang === "de" ? "Einladung gesendet" : "Invitation sent");
            onCreated();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="invite-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "inviteUser")}</DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">
                        {lang === "de" ? "Eine E-Mail mit Aktivierungslink wird verschickt. Empfänger setzt selbst sein Passwort." : lang === "tr" ? "Aktivasyon linki ile email gönderilir. Alıcı şifresini kendi belirler." : "An email with activation link is sent. The recipient sets their own password."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "name")}</Label>
                        <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "email")}</Label>
                        <Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "role")}</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="master">Master</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "spaces")}</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {spaces.map((s) => {
                                const active = spaceIds.includes(s.id);
                                return (
                                    <button key={s.id} type="button" data-testid={`invite-space-${s.id}`}
                                        onClick={() => setSpaceIds((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${active ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}>{s.name}</button>
                                );
                            })}
                        </div>
                    </div>
                    {link && (
                        <div className="p-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-xs">
                            <p className="font-semibold text-emerald-800 mb-1">{lang === "de" ? "Einladungslink (auch per Mail)" : "Invitation link"}:</p>
                            <code className="break-all text-emerald-900" data-testid="invite-link">{link}</code>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "close")}</Button>
                    <Button onClick={submit} disabled={busy || !name || !email} data-testid="invite-send-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        <Send className="w-4 h-4 mr-1" />{t(lang, "invite")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NewWorkerDialog({ open, onClose, onCreated, spaces, lang }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [role, setRole] = useState("user");
    const [spaceIds, setSpaceIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const [hrOpen, setHrOpen] = useState(false);
    const [hr, setHr] = useState({});

    useEffect(() => {
        if (open) { setName(""); setEmail(""); setPassword(""); setRole("user"); setSpaceIds([]); setShowPw(false); setHrOpen(false); setHr({ vacation_quota_yearly: 30 }); }
    }, [open]);

    const submit = async () => {
        if (!name.trim() || !email.trim() || !password) return;
        setBusy(true);
        try {
            const payload = { name, email, password, role, space_ids: spaceIds };
            // attach non-empty HR fields
            for (const k of Object.keys(hr)) {
                if (hr[k] !== "" && hr[k] !== null && hr[k] !== undefined) payload[k] = hr[k];
            }
            await api.post("/users", payload);
            toast.success(t(lang, "create"));
            onCreated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="new-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "addWorker")}</DialogTitle>
                    <DialogDescription className="sr-only">Add a new user</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "name")}</Label>
                        <Input data-testid="user-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "email")}</Label>
                        <Input data-testid="user-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "password")}</Label>
                        <div className="relative mt-1.5">
                            <Input
                                data-testid="user-password-input"
                                type={showPw ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="pr-9"
                            />
                            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-900" data-testid="user-password-toggle">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "role")}</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="master">Master</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "spaces")}</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {spaces.map((s) => {
                                const active = spaceIds.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        data-testid={`user-space-${s.id}`}
                                        onClick={() => setSpaceIds((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${active ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}
                                    >
                                        {s.name}
                                    </button>
                                );
                            })}
                            {spaces.length === 0 && <p className="text-xs text-neutral-400 italic">—</p>}
                        </div>
                    </div>

                    <HRFieldsSection lang={lang} hr={hr} setHr={setHr} open={hrOpen} setOpen={setHrOpen} testIdPrefix="new" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !name || !email || !password} data-testid="user-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditUserDialog({ user, onClose, onUpdated, spaces, lang, actor }) {
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [role, setRole] = useState("user");
    const [spaceIds, setSpaceIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const [hrOpen, setHrOpen] = useState(false);
    const [hr, setHr] = useState({});
    const [actingUntil, setActingUntil] = useState("");
    const [actingReason, setActingReason] = useState("");

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setPassword("");
            setRole(user.role || "user");
            setSpaceIds(user.space_ids || []);
            setShowPw(false);
            setHrOpen(false);
            setActingUntil(user.acting_until ? user.acting_until.slice(0, 10) : "");
            setActingReason(user.acting_reason || "");
            setHr({
                phone: user.phone || "",
                personnel_number: user.personnel_number || "",
                address: user.address || "",
                birthday: user.birthday || "",
                hire_date: user.hire_date || "",
                job_title: user.job_title || "",
                emergency_contact_name: user.emergency_contact_name || "",
                emergency_contact_phone: user.emergency_contact_phone || "",
                vacation_quota_yearly: user.vacation_quota_yearly ?? 30,
                notes: user.notes || "",
                superchat_channel_ids: user.superchat_channel_ids || [],
            });
        }
    }, [user]);

    if (!user) return null;

    const submit = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            const payload = { name, role, space_ids: spaceIds };
            if (password) payload.password = password;
            // include HR fields (always send so they can be cleared)
            for (const k of Object.keys(hr)) {
                payload[k] = hr[k] === "" ? null : hr[k];
            }
            // Acting-master (only admin can grant)
            if (actor?.role === "admin" && user.role !== "admin") {
                if (actingUntil) {
                    payload.acting_until = new Date(actingUntil + "T23:59:59Z").toISOString();
                    payload.acting_reason = actingReason || "";
                } else if (user.acting_until) {
                    // had it before, now empty → revoke
                    payload.acting_until = null;
                }
            }
            await api.patch(`/users/${user.id}`, payload);
            toast.success(t(lang, "save"));
            onUpdated();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="edit-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{t(lang, "editUser")}</DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">{user.email}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "name")}</Label>
                        <Input data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "password")}</Label>
                        <div className="relative mt-1.5">
                            <Input
                                data-testid="edit-user-password"
                                type={showPw ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={t(lang, "leaveBlankKeep")}
                                className="pr-9"
                            />
                            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-900" data-testid="edit-user-password-toggle">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="text-[11px] text-neutral-500 mt-1">{t(lang, "leaveBlankKeep")}</p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "role")}</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger className="mt-1.5" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="master">Master</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "spaces")}</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {spaces.map((s) => {
                                const active = spaceIds.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        data-testid={`edit-user-space-${s.id}`}
                                        onClick={() => setSpaceIds((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${active ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}
                                    >
                                        {s.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {actor?.role === "admin" && user.role !== "admin" && (
                        <div className="border-t border-neutral-200 pt-3" data-testid="acting-section">
                            <Label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                                <Crown className="w-3.5 h-3.5 text-amber-600" /> {lang === "de" ? "Urlaubsvertretung / Temp. Master-Rechte" : "Vacation Substitute / Temp Master"}
                            </Label>
                            <p className="text-[10px] text-neutral-500 mt-1">
                                {lang === "de" ? "Vergibt Master-Rechte bis zum gewählten Datum. Danach automatisch zurück auf User. Kann den Haupt-Admin NICHT löschen." : "Grants master rights until the chosen date. Auto-reverts to user. Cannot delete the main admin."}
                            </p>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                    <Label className="text-[10px] text-neutral-500">{lang === "de" ? "Aktiv bis" : "Active until"}</Label>
                                    <Input type="date" value={actingUntil} onChange={(e) => setActingUntil(e.target.value)} data-testid="acting-until" className="mt-1 h-8 text-xs" />
                                </div>
                                <div>
                                    <Label className="text-[10px] text-neutral-500">{lang === "de" ? "Grund (optional)" : "Reason (optional)"}</Label>
                                    <Input value={actingReason} onChange={(e) => setActingReason(e.target.value)} data-testid="acting-reason" placeholder={lang === "de" ? "Urlaubsvertretung Mario..." : "Vacation cover Mario..."} className="mt-1 h-8 text-xs" />
                                </div>
                            </div>
                            {user.acting_until && (
                                <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                                    {lang === "de" ? "Aktuell aktive Stellvertretung bis" : "Currently active until"}: <strong>{new Date(user.acting_until).toLocaleDateString(LOCALES[lang])}</strong>
                                    {user.acting_reason ? ` — ${user.acting_reason}` : ""}
                                    {" · "}
                                    <button type="button" onClick={() => setActingUntil("")} className="text-amber-700 hover:text-amber-900 underline" data-testid="acting-revoke">{lang === "de" ? "beenden" : "revoke"}</button>
                                </div>
                            )}
                        </div>
                    )}

                    <HRFieldsSection lang={lang} hr={hr} setHr={setHr} open={hrOpen} setOpen={setHrOpen} testIdPrefix="edit" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !name} data-testid="edit-user-save" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function HRFieldsSection({ lang, hr, setHr, open, setOpen, testIdPrefix }) {
    const upd = (k, v) => setHr((p) => ({ ...p, [k]: v }));
    const [scChannels, setScChannels] = useState([]);
    useEffect(() => {
        if (open) {
            api.get("/superchat/channels/all").then((r) => {
                setScChannels(r.data?.data?.results || []);
            }).catch(() => {});
        }
    }, [open]);
    const toggleCh = (id) => {
        const cur = hr.superchat_channel_ids || [];
        upd("superchat_channel_ids", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    };
    return (
        <div className="border-t border-neutral-200 pt-3">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                data-testid={`${testIdPrefix}-hr-toggle`}
                className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-neutral-700 hover:text-neutral-900"
            >
                <span className="flex items-center gap-1.5">
                    <UserCog className="w-3.5 h-3.5" />
                    {t(lang, "hrDetails")}
                </span>
                {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {open && (
                <div className="grid grid-cols-2 gap-3 mt-3" data-testid={`${testIdPrefix}-hr-fields`}>
                    <FieldInput label={t(lang, "phone")} value={hr.phone} onChange={(v) => upd("phone", v)} testId={`${testIdPrefix}-hr-phone`} placeholder="+49 ..." />
                    <FieldInput label={t(lang, "personnelNumber")} value={hr.personnel_number} onChange={(v) => upd("personnel_number", v)} testId={`${testIdPrefix}-hr-pn`} placeholder="PN-001" />
                    <FieldInput label={t(lang, "jobTitle")} value={hr.job_title} onChange={(v) => upd("job_title", v)} testId={`${testIdPrefix}-hr-job`} />
                    <FieldInput label={t(lang, "vacationQuota")} type="number" value={hr.vacation_quota_yearly} onChange={(v) => upd("vacation_quota_yearly", v ? parseInt(v, 10) : null)} testId={`${testIdPrefix}-hr-vac`} />
                    <FieldInput label={t(lang, "birthday")} type="date" value={hr.birthday} onChange={(v) => upd("birthday", v)} testId={`${testIdPrefix}-hr-bday`} />
                    <FieldInput label={t(lang, "hireDate")} type="date" value={hr.hire_date} onChange={(v) => upd("hire_date", v)} testId={`${testIdPrefix}-hr-hire`} />
                    <div className="col-span-2">
                        <FieldInput label={t(lang, "address")} value={hr.address} onChange={(v) => upd("address", v)} testId={`${testIdPrefix}-hr-addr`} />
                    </div>
                    <FieldInput label={t(lang, "emergencyContact")} value={hr.emergency_contact_name} onChange={(v) => upd("emergency_contact_name", v)} testId={`${testIdPrefix}-hr-ec-name`} />
                    <FieldInput label={t(lang, "emergencyPhone")} value={hr.emergency_contact_phone} onChange={(v) => upd("emergency_contact_phone", v)} testId={`${testIdPrefix}-hr-ec-phone`} />
                    <div className="col-span-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "notes")}</Label>
                        <Input value={hr.notes || ""} onChange={(e) => upd("notes", e.target.value)} data-testid={`${testIdPrefix}-hr-notes`} className="mt-1.5" />
                    </div>
                    {scChannels.length > 0 && (
                        <div className="col-span-2 border-t border-neutral-200 pt-3">
                            <Label className="text-xs font-semibold uppercase tracking-wider">💬 Superchat-Kanäle <span className="text-neutral-400 font-normal normal-case">({lang === "de" ? "Zugriff erlauben" : "allowed channels"})</span></Label>
                            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-hr-sc-channels`}>
                                {scChannels.map((ch) => {
                                    const sel = (hr.superchat_channel_ids || []).includes(ch.id);
                                    return (
                                        <button
                                            key={ch.id}
                                            type="button"
                                            onClick={() => toggleCh(ch.id)}
                                            data-testid={`${testIdPrefix}-sc-ch-${ch.id}`}
                                            className={`text-xs px-2 py-1 rounded-md border ${sel ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "border-neutral-300 hover:bg-neutral-50"}`}
                                        >
                                            {sel ? "✓ " : ""}{ch.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function FieldInput({ label, value, onChange, testId, type = "text", placeholder }) {
    return (
        <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
            <Input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                placeholder={placeholder}
                className="mt-1.5"
            />
        </div>
    );
}
